import { useState, useEffect, useCallback } from "react";
import { FileText, Loader2, Check, AlertCircle, X, Bell, ChevronDown, ChevronUp } from "lucide-react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";

/**
 * JobTracker - Shows background job status as a floating notification panel
 * Polls for job updates and shows progress
 */
export default function JobTracker({ onJobComplete }) {
  const [jobs, setJobs] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const [hasNewCompleted, setHasNewCompleted] = useState(false);

  // Fetch pending jobs
  const fetchJobs = useCallback(async () => {
    try {
      const response = await authFetch(`${API}/jobs/pending`);
      if (response.ok) {
        const data = await response.json();
        const newJobs = data.jobs || [];
        
        // Check if any jobs just completed
        jobs.forEach(oldJob => {
          const newJob = newJobs.find(j => j.job_id === oldJob.job_id);
          if (!newJob && oldJob.status === "processing") {
            // Job completed - fetch its final status
            fetchJobStatus(oldJob.job_id);
          }
        });
        
        setJobs(newJobs);
      }
    } catch (error) {
      console.error("Error fetching jobs:", error);
    }
  }, [jobs]);

  // Fetch single job status (for completed jobs)
  const fetchJobStatus = async (jobId) => {
    try {
      const response = await authFetch(`${API}/jobs/status/${jobId}`);
      if (response.ok) {
        const job = await response.json();
        if (job.status === "completed") {
          setHasNewCompleted(true);
          toast.success(
            <div className="flex flex-col">
              <span className="font-semibold">Documento elaborato!</span>
              <span className="text-sm opacity-80">{job.message}</span>
            </div>,
            { duration: 5000 }
          );
          onJobComplete?.(job);
        } else if (job.status === "error") {
          toast.error(
            <div className="flex flex-col">
              <span className="font-semibold">Errore elaborazione</span>
              <span className="text-sm opacity-80">{job.message}</span>
            </div>,
            { duration: 5000 }
          );
        }
      }
    } catch (error) {
      console.error("Error fetching job status:", error);
    }
  };

  // Poll for updates when there are pending jobs
  useEffect(() => {
    fetchJobs();
    
    const interval = setInterval(() => {
      fetchJobs();
    }, 3000); // Poll every 3 seconds
    
    return () => clearInterval(interval);
  }, []);

  // Re-poll when jobs change
  useEffect(() => {
    if (jobs.length > 0) {
      const interval = setInterval(fetchJobs, 2000);
      return () => clearInterval(interval);
    }
  }, [jobs.length, fetchJobs]);

  // Don't render if no jobs
  if (jobs.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm w-full">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-xl shadow-lg"
      >
        <div className="flex items-center gap-2">
          <div className="relative">
            <Bell size={18} />
            {hasNewCompleted && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            )}
          </div>
          <span className="font-medium">
            {jobs.length} document{jobs.length > 1 ? 'i' : 'o'} in elaborazione
          </span>
        </div>
        {expanded ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
      </button>

      {/* Jobs List */}
      {expanded && (
        <div className="bg-white dark:bg-[#1C1C1E] rounded-b-xl shadow-xl border border-t-0 border-gray-200 dark:border-gray-700 max-h-[300px] overflow-y-auto">
          {jobs.map(job => (
            <div 
              key={job.job_id} 
              className="p-4 border-b border-gray-100 dark:border-gray-800 last:border-b-0"
            >
              <div className="flex items-start gap-3">
                {/* Status Icon */}
                <div className="flex-shrink-0 mt-0.5">
                  {job.status === "pending" && (
                    <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                      <FileText className="text-gray-400" size={16} />
                    </div>
                  )}
                  {job.status === "processing" && (
                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <Loader2 className="text-blue-500 animate-spin" size={16} />
                    </div>
                  )}
                  {job.status === "completed" && (
                    <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <Check className="text-green-500" size={16} />
                    </div>
                  )}
                  {job.status === "error" && (
                    <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                      <AlertCircle className="text-red-500" size={16} />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-800 dark:text-gray-200 text-sm truncate">
                    Elaborazione documento
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {job.message}
                  </p>
                  
                  {/* Progress Bar */}
                  {(job.status === "pending" || job.status === "processing") && (
                    <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-indigo-500 h-1.5 rounded-full transition-all duration-500"
                        style={{ width: `${job.progress || 0}%` }}
                      />
                    </div>
                  )}
                </div>

                {/* Progress Percentage */}
                {(job.status === "pending" || job.status === "processing") && (
                  <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                    {job.progress || 0}%
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
